import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ACTIVITY_META, fmtDateTime, dateKey } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MagnifyingGlass, Plus, Phone, EnvelopeSimple, CalendarBlank, Note, CheckSquare } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

const ICONS = { apel: Phone, email: EnvelopeSimple, intalnire: CalendarBlank, nota: Note, task: CheckSquare };
const FILTERS = [
  { value: "all", label: "Toate" },
  { value: "apel", label: "Apeluri", Icon: Phone },
  { value: "email", label: "Email-uri", Icon: EnvelopeSimple },
  { value: "intalnire", label: "Întâlniri", Icon: CalendarBlank },
];

function NewActivityDialog({ leads, onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: "apel", title: "", description: "", lead_id: "" });
  const save = async () => {
    if (!f.title.trim()) return toast.error("Titlul este obligatoriu");
    try {
      const body = { type: f.type, title: f.title, description: f.description };
      if (f.lead_id) body.lead_id = f.lead_id;
      const { data } = await api.post("/activities", body);
      toast.success("Activitate adăugată");
      setOpen(false); setF({ type: "apel", title: "", description: "", lead_id: "" });
      onCreated(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="add-activity-btn"><Plus size={16} weight="bold" className="mr-1.5" /> Activitate Nouă</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Activitate nouă</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Tip</Label>
              <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                <SelectTrigger data-testid="activity-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ACTIVITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5"><Label>Lead (opțional)</Label>
              <Select value={f.lead_id || "none"} onValueChange={(v) => setF({ ...f, lead_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="activity-lead"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— fără lead —</SelectItem>
                  {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.company}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5"><Label>Titlu *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="activity-title" /></div>
          <div className="grid gap-1.5"><Label>Descriere</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="save-activity-btn">Salvează</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Activities() {
  const { hasRole } = useAuth();
  const [acts, setActs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const canEdit = hasRole("vanzari", "pm");

  const load = () => api.get("/activities").then((r) => setActs(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => acts.filter((a) => {
    if (filter !== "all" && a.type !== filter) return false;
    if (!q) return true;
    return `${a.title} ${a.description} ${a.lead_company} ${a.author_name}`.toLowerCase().includes(q.toLowerCase());
  }), [acts, q, filter]);

  const groups = useMemo(() => {
    const m = {};
    filtered.forEach((a) => { const k = dateKey(a.created_at); (m[k] = m[k] || []).push(a); });
    return Object.entries(m);
  }, [filtered]);

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 fade-up" data-testid="activities-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">CRM</p>
          <h1 className="font-head text-4xl font-extrabold tracking-tighter">Activități</h1>
          <p className="mt-1 text-sm text-muted-foreground">Înregistrează apeluri, email-uri și întâlniri</p>
        </div>
        {canEdit && <NewActivityDialog leads={leads} onCreated={(a) => setActs((l) => [a, ...l])} />}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Caută activități…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="activities-search" />
        </div>
        <div className="flex gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.value} onClick={() => setFilter(f.value)} data-testid={`activity-filter-${f.value}`}
              className={cn("flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors",
                filter === f.value ? "border border-primary/50 bg-primary/10 text-primary" : "text-muted-foreground hover:bg-white/5 hover:text-foreground")}>
              {f.Icon && <f.Icon size={15} />} {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        {groups.map(([day, items]) => (
          <div key={day}>
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{day}</div>
            <div className="space-y-2">
              {items.map((a) => {
                const meta = ACTIVITY_META[a.type] || ACTIVITY_META.nota;
                const Icon = ICONS[a.type] || Note;
                return (
                  <div key={a.id} className="flex items-start gap-4 rounded-xl border border-border bg-card/50 p-4 transition-colors hover:bg-card" data-testid={`activity-${a.id}`}>
                    <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", meta.cls)}><Icon size={19} weight="bold" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">{meta.label}</div>
                      <div className="text-sm text-muted-foreground">{a.title}</div>
                      {a.description && <div className="mt-0.5 text-sm text-muted-foreground/80">{a.description}</div>}
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground/70">
                        <span>{fmtDateTime(a.created_at)}</span>
                        {a.lead_company && <span>· {a.lead_company}</span>}
                        {a.author_name && <span>· de {a.author_name}</span>}
                        {a.done && <span className="text-emerald-400">· finalizat</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {groups.length === 0 && <p className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">Nicio activitate.</p>}
      </div>
    </div>
  );
}
